import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrdersTab({ password }) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [filter, setFilter] = useState("paid");

    useEffect(() => {
        const params = filter === "all" ? {} : { status: filter };
        axios
            .get(`${API}/admin/orders`, { params, headers: { "X-Admin-Password": password } })
            .then((r) => setOrders(r.data || []))
            .catch((e) => setError(e?.response?.data?.detail || "Failed to load orders"))
            .finally(() => setLoading(false));
    }, [password, filter]);

    const openLabel = (sessionId) => {
        const url = `${API}/admin/orders/${encodeURIComponent(sessionId)}/label.html`;
        // Open the label in a new tab. Add the admin header via a query param fallback for direct preview
        // (Note: browser tabs can't forward custom headers — for true print-from-tab we pass the password as a header
        //  via fetch and use a blob URL.)
        fetch(url, { headers: { "X-Admin-Password": password } })
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.text();
            })
            .then((html) => {
                const w = window.open("", "_blank", "width=900,height=720");
                if (w) {
                    w.document.write(html);
                    w.document.close();
                }
            })
            .catch((e) => alert(`Failed to load label: ${e.message}`));
    };

    const fmtMoney = (v) => {
        if (typeof v !== "number") return "—";
        // Backend stores amounts in dollars (e.g. 89.0) already — not Stripe cents
        return `$${v.toFixed(2)}`;
    };
    const fmtDate = (iso) => (iso ? iso.split("T")[0] : "—");

    return (
        <div data-testid="orders-tab" className="space-y-6">
            <div className="bg-fgm-surface border border-white/10 p-6">
                <div className="flex items-center gap-3 mb-3">
                    <div className="h-px w-12 bg-[#E63946]" />
                    <span className="font-display tracking-[0.3em] text-xs text-zinc-400 uppercase">
                        Order Fulfillment
                    </span>
                </div>
                <h3 className="font-display text-3xl text-white uppercase mb-1">Orders + Shipping</h3>
                <p className="text-sm text-zinc-400">
                    Click <strong className="text-white">🖨 Print Label</strong> to open a print-ready 4×6in shipping label with FROM/TO addresses and the items to pack.
                </p>
            </div>

            <div className="flex flex-wrap gap-2">
                {[
                    { id: "paid", label: "Paid" },
                    { id: "pending", label: "Pending" },
                    { id: "all", label: "All" },
                ].map((opt) => (
                    <button
                        key={opt.id}
                        type="button"
                        data-testid={`orders-filter-${opt.id}`}
                        onClick={() => setFilter(opt.id)}
                        className={`px-4 py-2 font-display tracking-[0.2em] text-xs uppercase border ${
                            filter === opt.id
                                ? "border-[#E63946] text-white bg-[#E63946]/10"
                                : "border-white/10 text-zinc-400 hover:text-white"
                        }`}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            {error && (
                <div className="bg-[#E63946]/10 border border-[#E63946] p-4 text-sm text-[#fbb]" data-testid="orders-error">
                    {error}
                </div>
            )}

            <div className="bg-fgm-surface border border-white/10 overflow-x-auto">
                {loading ? (
                    <div className="p-6 text-zinc-500 font-mono text-sm" data-testid="orders-loading">Loading…</div>
                ) : orders.length === 0 ? (
                    <div className="p-6 text-zinc-500 font-mono text-sm" data-testid="orders-empty">No orders yet — once Stripe payments land, they show up here.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="text-left text-zinc-500">
                            <tr className="text-[10px] font-display tracking-[0.25em] uppercase">
                                <th className="px-4 py-3">Date</th>
                                <th className="px-4 py-3">Customer</th>
                                <th className="px-4 py-3">Ship To</th>
                                <th className="px-4 py-3 text-right">Total</th>
                                <th className="px-4 py-3 text-right">Items</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3 text-right">Label</th>
                            </tr>
                        </thead>
                        <tbody className="text-white">
                            {orders.map((o) => (
                                <tr key={o.session_id || o.id} className="border-t border-white/5" data-testid={`order-row-${o.session_id}`}>
                                    <td className="px-4 py-3 font-mono text-xs text-zinc-400">{fmtDate(o.created_at)}</td>
                                    <td className="px-4 py-3">
                                        <div className="text-white">{o.shipping_name || o.customer_email}</div>
                                        {o.shipping_name && (
                                            <div className="text-[10px] text-zinc-500 font-mono">{o.customer_email}</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-zinc-300">
                                        {o.has_shipping ? (
                                            <>
                                                {o.ship_to_city}
                                                {o.ship_to_state ? `, ${o.ship_to_state}` : ""}
                                                {o.ship_to_country && o.ship_to_country !== "US" ? ` · ${o.ship_to_country}` : ""}
                                            </>
                                        ) : (
                                            <span className="text-zinc-600 italic">no address</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono">{fmtMoney(o.amount_total)}</td>
                                    <td className="px-4 py-3 text-right text-xs text-zinc-400">{o.items_count || "?"}</td>
                                    <td className="px-4 py-3">
                                        <span
                                            className={`text-[10px] font-display tracking-[0.15em] uppercase px-2 py-1 ${
                                                o.payment_status === "paid"
                                                    ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/40"
                                                    : "bg-yellow-500/10 text-yellow-400 border border-yellow-500/40"
                                            }`}
                                        >
                                            {o.payment_status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            type="button"
                                            data-testid={`order-print-${o.session_id}`}
                                            onClick={() => openLabel(o.session_id)}
                                            disabled={!o.has_shipping || o.payment_status !== "paid"}
                                            title={!o.has_shipping ? "No shipping address on file" : o.payment_status !== "paid" ? "Only paid orders can print" : "Print 4×6 shipping label"}
                                            className="text-[10px] font-display tracking-[0.2em] uppercase px-3 py-2 bg-fgm-dark border border-white/20 hover:border-[#E63946] hover:text-[#E63946] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                        >
                                            🖨 Print Label
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
